import React, { useState, useEffect, useMemo, useRef } from "react";
import Modal from "react-bootstrap/Modal";

const CURRENCIES = ["UGX", "USD", "EUR", "CAD"];

const FieldLabel = ({ children }) => (
  <div
    style={{
      color: "#6b7280",
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      marginBottom: 8,
    }}
  >
    {children}
  </div>
);

const inputStyle = {
  width: "100%",
  backgroundColor: "#fff",
  border: "1.5px solid #e5e7eb",
  borderRadius: 10,
  padding: "10px 14px",
  fontSize: 14,
  color: "#111827",
  outline: "none",
  transition: "border-color 0.15s, box-shadow 0.15s",
};

const focusHandlers = {
  onFocus: (e) => {
    e.target.style.borderColor = "#ff5100";
    e.target.style.boxShadow = "0 0 0 3px rgba(255,81,0,0.1)";
  },
  onBlur: (e) => {
    e.target.style.borderColor = "#e5e7eb";
    e.target.style.boxShadow = "none";
  },
};

const SectionDivider = ({ label }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      margin: "20px 0 16px",
    }}
  >
    <div style={{ flex: 1, height: 1, background: "#f3f4f6" }} />
    <span
      style={{
        fontSize: 11,
        fontWeight: 700,
        color: "#9ca3af",
        letterSpacing: "0.06em",
        textTransform: "uppercase",
      }}
    >
      {label}
    </span>
    <div style={{ flex: 1, height: 1, background: "#f3f4f6" }} />
  </div>
);

const CreateOrderAssignment = ({
  domain,
  show,
  handleClose,
  handleFormSubmit,
  orderId,
  modalData,
}) => {
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [clientSearch, setClientSearch] = useState("");
  const [clientOpen, setClientOpen] = useState(false);
  const clientDropdownRef = useRef(null);
  const [description, setDescription] = useState("");
  const [minSerial, setMinSerial] = useState("");
  const [maxSerial, setMaxSerial] = useState("");
  const [userEmail] = useState("adrenreuel@gmail.com");
  const [isMinSerialDisabled, setIsMinSerialDisabled] = useState(false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  const [serialRangeUpperBound, setSerialRangeUpperBound] = useState(null);
  const [maxSerialError, setMaxSerialError] = useState("");

  const [currency, setCurrency] = useState("");
  const [unitCost, setUnitCost] = useState("");
  const [additionalCosts, setAdditionalCosts] = useState([]);
  const [discount, setDiscount] = useState("");
  const tax = 18;

  useEffect(() => {
    if (!show) {
      // Reset bound on close so validation deps change on the next open
      setSerialRangeUpperBound(null);
      return;
    }

    const fetchClients = async () => {
      try {
        const response = await fetch("/api/organizations");
        const result = await response.json();
        setClients(result.filter((org) => org.type === "brand"));
      } catch (error) {
        console.error("Failed to fetch clients:", error);
      }
    };

    fetchClients();
    resetForm();

    const serialRange = modalData?.order?.serialRange;
    if (serialRange) {
      const [minSerialFromRange, maxSerialFromRange] = serialRange.split(" - ");
      const minSerialNumber = parseInt(minSerialFromRange.trim(), 10);
      setMinSerial(minSerialNumber);

      const maxSerialParsed = parseInt(maxSerialFromRange.trim(), 10);
      if (isNaN(maxSerialParsed)) {
        console.error("Invalid maxSerial:", maxSerialFromRange);
        setMaxSerialError("Invalid max serial range.");
        setIsSubmitDisabled(true);
        return;
      }

      setSerialRangeUpperBound(maxSerialParsed);
      setIsMinSerialDisabled(true);
      setMaxSerial(maxSerialParsed);
    }

    if (modalData?.order?.assignments?.length === 0) {
      // no-op for empty assignments
    } else {
      const mostRecentAssignment = modalData?.order?.assignments
        ?.slice()
        .sort((a, b) => new Date(b.date) - new Date(a.date))[0];

      if (mostRecentAssignment) {
        setMinSerial(mostRecentAssignment.maxSerial + 1);
        setIsMinSerialDisabled(true);
      }
    }
  }, [show, modalData]);

  // Validate serial range on init and whenever inputs change
  useEffect(() => {
    if (maxSerial === "" || minSerial === "" || serialRangeUpperBound === null) return;
    const parsedMax = parseInt(maxSerial, 10);
    const parsedMin = parseInt(minSerial, 10);
    if (isNaN(parsedMax)) {
      setMaxSerialError("Please enter a valid number for Max Serial.");
      setIsSubmitDisabled(true);
    } else if (parsedMax > serialRangeUpperBound) {
      setMaxSerialError(`Max serial cannot exceed ${serialRangeUpperBound}.`);
      setIsSubmitDisabled(true);
    } else if (parsedMax < parsedMin) {
      setMaxSerialError("Max serial cannot be less than Min serial.");
      setIsSubmitDisabled(true);
    } else {
      setMaxSerialError("");
      setIsSubmitDisabled(false);
    }
  }, [minSerial, maxSerial, serialRangeUpperBound]);

  const filteredClients = useMemo(() => {
    const q = clientSearch.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) =>
      (c.organization || "").toLowerCase().includes(q)
    );
  }, [clients, clientSearch]);

  useEffect(() => {
    if (!clientOpen) return;
    const handleOutside = (e) => {
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(e.target)) {
        setClientOpen(false);
      }
    };
    const handleEsc = (e) => { if (e.key === "Escape") setClientOpen(false); };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [clientOpen]);

  const handleMaxSerialChange = (e) => {
    setMaxSerial(e.target.value);
  };

  const handleSubmit = async () => {
    const client = clients.find((c) => c._id === selectedClientId);
    if (!client || !description || !minSerial || !maxSerial) {
      alert("Please fill all required fields.");
      return;
    }
    if (!currency) {
      alert("Please select a currency.");
      return;
    }

    const payload = {
      order: orderId,
      client: { id: client._id, name: client.organization },
      description,
      minSerial: parseInt(minSerial),
      maxSerial: parseInt(maxSerial),
      user: userEmail,
      currency,
      ...(unitCost !== "" && { unitCost: parseFloat(unitCost) }),
      additionalCosts: additionalCosts
        .filter((c) => c.label.trim() && c.amount !== "")
        .map((c) => ({ label: c.label.trim(), amount: parseFloat(c.amount) })),
      ...(discount !== "" && { discount: parseFloat(discount) }),
      ...(tax !== "" && { tax: parseFloat(tax) }),
    };

    try {
      const response = await fetch(
        "https://api.iriscrypt.com/orderAssignment",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": "f87f030d-eaf4-4a39-9d5d-c9ffdb90ab43",
          },
          body: JSON.stringify(payload),
        },
      );

      const result = await response.json();
      if (response.ok && result.success) {
        resetForm();
        handleClose();
        handleFormSubmit();
      } else {
        alert(result?.detail || "Failed to create assignment.");
      }
    } catch (error) {
      console.error("Assignment submit error:", error);
      alert("An error occurred.");
    }
  };

  const addAdditionalCost = () =>
    setAdditionalCosts((prev) => [...prev, { label: "", amount: "" }]);

  const removeAdditionalCost = (index) =>
    setAdditionalCosts((prev) => prev.filter((_, i) => i !== index));

  const updateAdditionalCost = (index, field, value) =>
    setAdditionalCosts((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    );

  const costPreview = useMemo(() => {
    const qty =
      parseInt(minSerial, 10) && parseInt(maxSerial, 10)
        ? parseInt(maxSerial, 10) - parseInt(minSerial, 10) + 1
        : 0;
    const unit = parseFloat(unitCost) || 0;
    const addons = additionalCosts.reduce(
      (s, c) => s + (parseFloat(c.amount) || 0),
      0,
    );
    const subtotal = unit * qty + addons;
    const afterDiscount = subtotal * (1 - (parseFloat(discount) || 0) / 100);
    const total = afterDiscount * (1 + (parseFloat(tax) || 0) / 100);
    return { qty, subtotal, afterDiscount, total };
  }, [minSerial, maxSerial, unitCost, additionalCosts, discount, tax]);

  const fmt = (n) =>
    Number.isFinite(n)
      ? n.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "0.00";

  const resetForm = () => {
    setSelectedClientId("");
    setClientSearch("");
    setClientOpen(false);
    setDescription("");
    setMinSerial("");
    setMaxSerial("");
    setIsMinSerialDisabled(false);
    setMaxSerialError("");
    setIsSubmitDisabled(false);
    setCurrency("");
    setUnitCost("");
    setAdditionalCosts([]);
    setDiscount("");
  };

  return (
    <>
      <style>{`
        .order-assignment-modal .modal-content {
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 18px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.12);
        }
        .order-assignment-modal .modal-header {
          background: #fff;
          border-bottom: 1px solid #f3f4f6;
          border-radius: 18px 18px 0 0;
          padding: 20px 24px 16px;
        }
        .order-assignment-modal .modal-body {
          background: #fff;
          padding: 20px 24px;
        }
        .order-assignment-modal .modal-footer {
          background: #f9fafb;
          border-top: 1px solid #f3f4f6;
          border-radius: 0 0 18px 18px;
          padding: 16px 24px;
        }
      `}</style>

      <Modal
        show={show}
        onHide={handleClose}
        backdrop="static"
        dialogClassName="order-assignment-modal"
        centered
        style={{ backdropFilter: "blur(3px)" }}
      >
        <Modal.Header closeButton>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: 9,
                background: "rgba(255,81,0,0.08)",
                border: "1.5px solid rgba(255,81,0,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <i
                className="fas fa-link"
                style={{ color: "#ff5100", fontSize: 13 }}
              />
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16, color: "#111827" }}>
                Assign to Client
              </div>
              <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 1 }}>
                Allocate tags from this order to a client
              </div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body>
          {/* Client */}
          <div ref={clientDropdownRef} style={{ position: "relative" }}>
            <FieldLabel>Client *</FieldLabel>
            <div
              onClick={() => setClientOpen((o) => !o)}
              style={{
                ...inputStyle,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                userSelect: "none",
                borderColor: clientOpen ? "#ff5100" : "#e5e7eb",
                boxShadow: clientOpen ? "0 0 0 3px rgba(255,81,0,0.1)" : "none",
              }}
            >
              <span style={{ color: selectedClientId ? "#111827" : "#9ca3af" }}>
                {selectedClientId
                  ? (clients.find((c) => c._id === selectedClientId)?.organization || "Select a client…")
                  : "Select a client…"}
              </span>
              <i
                className={`fas fa-chevron-${clientOpen ? "up" : "down"}`}
                style={{ fontSize: 11, color: "#9ca3af", flexShrink: 0, marginLeft: 8 }}
              />
            </div>

            {clientOpen && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 4px)",
                  left: 0,
                  right: 0,
                  background: "#fff",
                  border: "1.5px solid #e5e7eb",
                  borderRadius: 12,
                  boxShadow: "0 10px 30px rgba(0,0,0,0.10)",
                  zIndex: 100,
                  overflow: "hidden",
                }}
              >
                <div style={{ padding: "8px 10px", borderBottom: "1px solid #f3f4f6" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#f9fafb", border: "1.5px solid #e5e7eb", borderRadius: 8, padding: "7px 10px" }}>
                    <i className="fas fa-search" style={{ color: "#9ca3af", fontSize: 12, flexShrink: 0 }} />
                    <input
                      autoFocus
                      type="text"
                      value={clientSearch}
                      onChange={(e) => setClientSearch(e.target.value)}
                      placeholder="Search clients…"
                      style={{ border: "none", outline: "none", background: "transparent", fontSize: 13, color: "#111827", width: "100%" }}
                    />
                    {clientSearch && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setClientSearch(""); }}
                        style={{ border: "none", background: "none", cursor: "pointer", color: "#9ca3af", padding: 0, fontSize: 12, display: "flex", alignItems: "center" }}
                      >
                        <i className="fas fa-times" />
                      </button>
                    )}
                  </div>
                </div>

                <div style={{ maxHeight: 220, overflowY: "auto" }}>
                  {filteredClients.length === 0 ? (
                    <div style={{ padding: "14px", fontSize: 13, color: "#9ca3af", textAlign: "center" }}>
                      No clients match "{clientSearch}"
                    </div>
                  ) : (
                    filteredClients.map((client) => {
                      const isSelected = client._id === selectedClientId;
                      return (
                        <div
                          key={client._id}
                          onClick={() => {
                            setSelectedClientId(client._id);
                            setClientOpen(false);
                            setClientSearch("");
                          }}
                          style={{
                            padding: "10px 14px",
                            cursor: "pointer",
                            background: isSelected ? "rgba(255,81,0,0.06)" : "transparent",
                            borderLeft: isSelected ? "3px solid #ff5100" : "3px solid transparent",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                          onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = "#f9fafb"; }}
                          onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}
                        >
                          <span style={{ fontSize: 13, fontWeight: 700, color: isSelected ? "#ff5100" : "#111827" }}>
                            {client.organization}
                          </span>
                          {isSelected && (
                            <i className="fas fa-check" style={{ fontSize: 12, color: "#ff5100", flexShrink: 0 }} />
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Description */}
          <div style={{ marginTop: 16 }}>
            <FieldLabel>Description *</FieldLabel>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of this assignment…"
              rows={3}
              style={{ ...inputStyle, resize: "vertical", minHeight: 80 }}
              {...focusHandlers}
            />
          </div>

          {/* Serial range — unified container */}
          <div style={{ marginTop: 16 }}>
            <FieldLabel>Serial Range *</FieldLabel>
            <div
              style={{
                display: "flex",
                border: "1.5px solid #e5e7eb",
                borderRadius: 10,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  flex: 1,
                  padding: "10px 14px",
                  background: isMinSerialDisabled ? "#f9fafb" : "#fff",
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: "#9ca3af",
                    letterSpacing: "0.05em",
                    marginBottom: 4,
                  }}
                >
                  FROM
                </div>
                <input
                  type="number"
                  value={minSerial}
                  onChange={(e) => setMinSerial(e.target.value)}
                  disabled={isMinSerialDisabled}
                  placeholder="Min serial"
                  style={{
                    width: "100%",
                    border: "none",
                    outline: "none",
                    fontSize: 14,
                    fontWeight: 600,
                    color: isMinSerialDisabled ? "#6b7280" : "#111827",
                    background: "transparent",
                    padding: 0,
                  }}
                />
              </div>
              <div
                style={{
                  width: 1,
                  background: "#e5e7eb",
                  alignSelf: "stretch",
                }}
              />
              <div style={{ flex: 1, padding: "10px 14px" }}>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: "#9ca3af",
                    letterSpacing: "0.05em",
                    marginBottom: 4,
                  }}
                >
                  TO
                </div>
                <input
                  type="number"
                  value={maxSerial}
                  onChange={handleMaxSerialChange}
                  placeholder="Max serial"
                  style={{
                    width: "100%",
                    border: "none",
                    outline: "none",
                    fontSize: 14,
                    fontWeight: 600,
                    color: "#111827",
                    background: "transparent",
                    padding: 0,
                  }}
                />
              </div>
            </div>
            {maxSerialError && (
              <div
                style={{
                  marginTop: 6,
                  fontSize: 12,
                  color: "#dc2626",
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                }}
              >
                <i
                  className="fas fa-exclamation-circle"
                  style={{ fontSize: 11 }}
                />
                {maxSerialError}
              </div>
            )}
          </div>

          <SectionDivider label="Pricing" />

          {/* Currency */}
          <div>
            <FieldLabel>Currency *</FieldLabel>
            <div style={{ display: "flex", gap: 8 }}>
              {CURRENCIES.map((c) => {
                const selected = currency === c;
                return (
                  <button
                    key={c}
                    onClick={() => setCurrency(c)}
                    style={{
                      flex: 1,
                      padding: "9px 0",
                      borderRadius: 10,
                      border: `1.5px solid ${selected ? "#ff5100" : "#e5e7eb"}`,
                      background: selected ? "rgba(255,81,0,0.06)" : "#fff",
                      fontWeight: 700,
                      fontSize: 13,
                      color: selected ? "#ff5100" : "#6b7280",
                      cursor: "pointer",
                      transition:
                        "border-color 0.15s, background 0.15s, color 0.15s",
                    }}
                  >
                    {c}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Unit cost */}
          <div style={{ marginTop: 16 }}>
            <FieldLabel>Unit Cost (per tag)</FieldLabel>
            <input
              type="number"
              min="0"
              step="any"
              value={unitCost}
              onChange={(e) => setUnitCost(e.target.value)}
              placeholder="0.00"
              style={inputStyle}
              {...focusHandlers}
            />
          </div>

          {/* Additional costs */}
          {additionalCosts.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <FieldLabel>Additional Costs</FieldLabel>
              {additionalCosts.map((item, index) => (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "center",
                    marginBottom: 8,
                  }}
                >
                  <input
                    type="text"
                    value={item.label}
                    onChange={(e) =>
                      updateAdditionalCost(index, "label", e.target.value)
                    }
                    placeholder="Label (e.g. Design)"
                    style={{ ...inputStyle, flex: 2 }}
                    {...focusHandlers}
                  />
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={item.amount}
                    onChange={(e) =>
                      updateAdditionalCost(index, "amount", e.target.value)
                    }
                    placeholder="Amount"
                    style={{ ...inputStyle, flex: 1 }}
                    {...focusHandlers}
                  />
                  <button
                    onClick={() => removeAdditionalCost(index)}
                    title="Remove"
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 8,
                      border: "1.5px solid #fecaca",
                      background: "#fff5f5",
                      color: "#dc2626",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <i
                      className="fas fa-times"
                      style={{ fontSize: 13 }}
                    />
                  </button>
                </div>
              ))}
            </div>
          )}

          <button
            onClick={addAdditionalCost}
            style={{
              marginTop: additionalCosts.length > 0 ? 4 : 16,
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              padding: "8px 14px",
              borderRadius: 9,
              border: "1.5px dashed #d1d5db",
              background: "transparent",
              color: "#6b7280",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              transition: "border-color 0.15s, color 0.15s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "#ff5100";
              e.currentTarget.style.color = "#ff5100";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "#d1d5db";
              e.currentTarget.style.color = "#6b7280";
            }}
          >
            <i
              className="fas fa-plus"
              style={{ fontSize: 11 }}
            />
            Add additional cost
          </button>

          {/* Discount + Tax */}
          <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
            <div style={{ flex: 1 }}>
              <FieldLabel>Discount (%)</FieldLabel>
              <input
                type="number"
                min="0"
                max="100"
                step="any"
                value={discount}
                onChange={(e) => setDiscount(e.target.value)}
                placeholder="0"
                style={inputStyle}
                {...focusHandlers}
              />
            </div>
            <div style={{ flex: 1 }}>
              <FieldLabel>VAT Tax (%)</FieldLabel>
              <div
                style={{
                  ...inputStyle,
                  background: "#f9fafb",
                  color: "#6b7280",
                  fontWeight: 600,
                  cursor: "not-allowed",
                }}
              >
                {tax}%
              </div>
            </div>
          </div>

          {/* Cost preview */}
          {(unitCost !== "" || additionalCosts.length > 0) && (
            <div
              style={{
                marginTop: 16,
                background: "#f9fafb",
                border: "1.5px solid #e5e7eb",
                borderRadius: 12,
                padding: "14px 16px",
                fontSize: 13,
              }}
            >
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 11,
                  color: "#9ca3af",
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  marginBottom: 10,
                }}
              >
                Cost Preview
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 6,
                }}
              >
                <span style={{ color: "#6b7280" }}>Quantity</span>
                <span style={{ fontWeight: 600, color: "#111827" }}>
                  {costPreview.qty.toLocaleString()} tags
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 6,
                }}
              >
                <span style={{ color: "#6b7280" }}>Subtotal</span>
                <span style={{ fontWeight: 600, color: "#111827" }}>
                  {currency} {fmt(costPreview.subtotal)}
                </span>
              </div>
              {parseFloat(discount) > 0 && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 6,
                  }}
                >
                  <span style={{ color: "#6b7280" }}>
                    After {discount}% discount
                  </span>
                  <span style={{ fontWeight: 600, color: "#111827" }}>
                    {currency} {fmt(costPreview.afterDiscount)}
                  </span>
                </div>
              )}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontWeight: 800,
                  fontSize: 14,
                  borderTop: "1.5px solid #e5e7eb",
                  paddingTop: 10,
                  marginTop: 6,
                  color: "#111827",
                }}
              >
                <span>
                  Total {parseFloat(tax) > 0 ? `(incl. ${tax}% VAT)` : ""}
                </span>
                <span style={{ color: "#ff5100" }}>
                  {currency} {fmt(costPreview.total)}
                </span>
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            style={{
              width: "100%",
              padding: "11px 0",
              borderRadius: 10,
              border: "none",
              background: isSubmitDisabled
                ? "#e5e7eb"
                : "linear-gradient(135deg, #c13d00 0%, #ff5100 100%)",
              color: isSubmitDisabled ? "#9ca3af" : "#fff",
              fontWeight: 700,
              fontSize: 15,
              cursor: isSubmitDisabled ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 9,
              transition: "background 0.15s",
            }}
          >
            Assign to Client
            <i
              className="fas fa-arrow-right"
              style={{ fontSize: 13 }}
            />
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CreateOrderAssignment;
